import { OfflineCard } from "./OfflineCard";
import { canApprove, canSeeFinancials, useMe } from "./session";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { measurementChanges, newItemId, realWorldValue, sheetNumberText, takeoffGroupKey, type Measurement, PIN_TYPES, PIN_COLORS, type CenterlineDocument, type Pin, type PinType } from "@centerline/core/viewer";
import { api, type DocumentSummary, type ProjectSummary, type UploadResult } from "./api";
import { Header } from "./Header";
import { href, navigate } from "./router";
import { markRecent } from "./Projects";
import { useLiveNotices, useProjectLive, type Presence } from "./live";
import { DailyReports } from "./DailyReports";
import { DropFiles } from "./DropFiles";
import { Modal, type ModalSpec } from "./Modal";
import { actorName, clientId } from "./useDocSync";
import { SovTab } from "./SovTab";
import { JobFiles } from "./JobFiles";
import { CopsTab } from "./Cops";
import { JobReportTab } from "./JobReport";
import { SubmittalsTab } from "./Submittals";
import { PunchListTab } from "./PunchList";

const KIND_LABEL: Record<string, string> = { pdf: "PDF", "centerline-project": ".centerline project", "centerline-pdf": "Centerline PDF" };
const ACCEPT = ".pdf,.centerline,.watsontakeoff,application/pdf";
export const TABS = [["plans", "Plans"], ["takeoffs", "Takeoffs"], ["markups", "Markups"], ["sov", "SOV"], ["dailies", "Dailies"], ["pins", "Pins"], ["punch", "Punch List"], ["submittals", "Submittals"], ["files", "Files"], ["cops", "Change Orders"], ["report", "Report"]] as const;
type Tab = (typeof TABS)[number][0];

export function PresenceDots({ people, me }: { people: Presence[]; me: string }) {
  const others = people.filter((p) => p.clientId !== me);
  if (!others.length) return null;
  return (
    <div className="presence" title={others.map((p) => `${p.name}${p.sheet ? ` — on ${p.sheet}` : ""}`).join("\n")}>
      {others.slice(0, 5).map((p) => <span key={p.clientId} className="avatar" style={{ background: colorFor(p.name) }}>{initials(p.name)}</span>)}
      <span className="small">{others.length === 1 ? `${others[0]!.name} online` : `${others.length} online`}</span>
    </div>
  );
}
export const initials = (n: string) => n.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("") || "?";
export const colorFor = (n: string) => `hsl(${[...n].reduce((a, c) => a + c.charCodeAt(0) * 7, 0) % 360} 55% 42%)`;

export function ProjectPage({ id, tab: tabParam, onSignOut }: { id: string; tab?: string; onSignOut: () => void }) {
  const fin = canSeeFinancials(useMe());
  const tabs = TABS.filter(([k]) => fin || (k !== "sov" && k !== "report" && k !== "files" && k !== "cops"));
  const tab: Tab = (tabs.find(([k]) => k === tabParam)?.[0] ?? "plans");
  const [project, setProject] = useState<ProjectSummary | null>(null);
  const [documents, setDocuments] = useState<DocumentSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { notices, push } = useLiveNotices();
  const [tick, setTick] = useState(0);

  const load = useCallback(() => api.project(id).then((r) => { setProject(r.project); setDocuments(r.documents); }).catch((e) => setError(e.message)), [id]);
  useEffect(() => { void load(); markRecent(id); }, [load, id]);
  const live = useProjectLive(id, (c) => {
    if (c.kind === "document" || c.kind === "project") void load();
    setTick((t) => t + 1);
    const what = { pin: "a pin", sov: "the schedule of values", dailyReport: "a daily report", document: "plans/markups", project: "the project" }[c.kind];
    push(`${c.by} ${c.action} ${what}${c.conflict ? " (overwrote a simultaneous edit)" : ""}`, c.conflict ? "warn" : "info");
  }, { onResync: () => { void load(); setTick((t) => t + 1); }, view: { sheet: `${TABS.find(([k]) => k === tab)?.[1]} tab` } });

  return (
    <div className="page">
      <Header onSignOut={onSignOut}><a href="#/">Projects</a> <span className="sep">/</span> <span>{project?.name ?? "…"}</span></Header>
      <div className="ws-bar">
        <h2 className="ws-title">{project?.name ?? "…"}{project?.archivedAt && <span className="status-chip">archived</span>}</h2>
        <PresenceDots people={live.presence} me={live.me} />
        <span className={`live-dot ${live.status}`} title={live.status === "live" ? "Live: changes appear instantly" : "Reconnecting…"}>{live.status === "live" ? "● Live" : "○ Reconnecting"}</span>
      </div>
      <nav className="tabs" role="tablist">
        {tabs.map(([k, label]) => <a key={k} role="tab" aria-selected={tab === k} className={tab === k ? "on" : ""} href={href({ name: "project", id, tab: k })}>{label}</a>)}
      </nav>
      <main className="content ws-content">
        {error && <div className="error">{error}</div>}
        {tab === "plans" && <PlansTab id={id} documents={documents} reload={load} />}
        {tab === "takeoffs" && <TakeoffsTab documents={documents} tick={tick} />}
        {tab === "markups" && <MarkupsTab documents={documents} tick={tick} />}
        {tab === "sov" && <SovTab projectId={id} tick={tick} />}
        {tab === "submittals" && <SubmittalsTab projectId={id} documents={documents} tick={tick} />}
        {tab === "dailies" && <DailyReports projectId={id} tick={tick} />}
        {tab === "files" && <JobFiles projectId={id} tick={tick} />}
        {tab === "cops" && <CopsTab projectId={id} projectName={project?.name ?? ""} />}
        {tab === "report" && <JobReportTab projectId={id} tick={tick} />}
        {tab === "punch" && <PunchListTab projectId={id} tick={tick} />}
        {tab === "pins" && <PinsTab projectId={id} documents={documents} tick={tick} />}
      </main>
      <div className="notices" aria-live="polite">{notices.map((n) => <div key={n.id} className={`notice ${n.kind}`}>{n.text}</div>)}</div>
    </div>
  );
}

function PlansTab({ id, documents, reload }: { id: string; documents: DocumentSummary[]; reload: () => Promise<unknown> }) {
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const office = canApprove(useMe());
  const input = useRef<HTMLInputElement>(null);
  const archivedCount = documents.filter((d) => d.archivedAt).length;
  const shown = documents.filter((d) => (showArchived ? !!d.archivedAt : !d.archivedAt));
  const act = async (fn: () => Promise<unknown>) => { setError(null); try { await fn(); await reload(); } catch (err) { setError((err as Error).message); } };
  const upload = async (files: File[]) => {
    if (!files.length) return;
    setResult(null); setError(null); setProgress(0);
    try { setResult(await api.upload(id, files, setProgress)); await reload(); }
    catch (err) { setError((err as Error).message); }
    finally { setProgress(null); }
  };
  return (
    <>
      <OfflineCard projectId={id} documents={documents} />
      <DropFiles testId="drop-plans" accept={ACCEPT} kind="a PDF plan set or Centerline project" disabled={progress !== null} label="Drop plan PDFs here" onFiles={(fs) => void upload(fs)}>
      <div className="dropzone" onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); input.current?.click(); } }} onClick={() => input.current?.click()} role="button" tabIndex={0}>
        <input ref={input} type="file" multiple accept={ACCEPT} hidden onChange={(e) => { void upload([...(e.target.files ?? [])]); e.target.value = ""; }} />
        {progress === null ? <><strong>Upload plans</strong> — tap to choose files (or drop them here).
          <div className="muted small">PDF plan sets, Centerline <code>.centerline</code> projects and Centerline PDFs. Plain PDFs arrive unscaled: calibrate or pick a scale per sheet.</div></>
          : <div className="progress"><div style={{ width: `${Math.round(progress * 100)}%` }} /><span>{progress < 1 ? `Uploading ${Math.round(progress * 100)}%` : "Processing…"}</span></div>}
      </div>
      </DropFiles>
      {error && <div className="error">{error}</div>}
      {result && <div className="notice">
        {result.created.map((c) => <div key={c.document.id}>✓ Added <strong>{c.document.name}</strong> ({KIND_LABEL[c.document.kind]}, {c.document.pageCount} sheets){c.warnings.map((w, i) => <div key={i} className="muted small">• {w}</div>)}</div>)}
        {result.failed.map((f, i) => <div key={i} className="error-text">✗ {f.fileName}: {f.message}</div>)}
      </div>}
      {documents.length === 0 && <div className="empty">No plans in this project yet.</div>}
      {archivedCount > 0 && <div className="plan-filter">
        <button type="button" className={`btn small ${showArchived ? "" : "primary"}`} aria-pressed={!showArchived} onClick={() => setShowArchived(false)}>Current</button>
        <button type="button" className={`btn small ${showArchived ? "primary" : ""}`} aria-pressed={showArchived} onClick={() => setShowArchived(true)}>Archived ({archivedCount})</button>
      </div>}
      {showArchived && shown.length === 0 && <div className="empty">No archived plan sets.</div>}
      <div className="grid plan-grid">
        {shown.map((d) => (
          <div key={d.id} className={`card plan-card${d.working ? " working" : ""}${d.archivedAt ? " archived" : ""}`}>
            <a className="plan-link" href={href({ name: "viewer", id: d.id, page: 1 })}>
              <div className="title">{d.name}{d.working && <span className="status-chip approved">Working set</span>}{d.archivedAt && <span className="status-chip">Archived</span>}</div>
              <div className="muted small">{d.pageCount} sheet{d.pageCount === 1 ? "" : "s"} · <span className={`badge kind-${d.kind}`}>{KIND_LABEL[d.kind]}</span></div>
              <div className="muted small">Uploaded {new Date(d.createdAt).toLocaleDateString()}{d.archivedAt ? ` · Archived ${new Date(d.archivedAt).toLocaleDateString()}` : ""}</div>
            </a>
            {office && <div className="plan-actions">
              {!d.archivedAt && !d.working && <button type="button" className="btn small" onClick={() => void act(() => api.setWorkingDocument(d.id, true))}>Make working set</button>}
              {d.archivedAt
                ? <button type="button" className="btn small" onClick={() => void act(() => api.archiveDocument(d.id, false))}>Restore</button>
                : <button type="button" className="btn small" onClick={() => { if (confirm(`Archive "${d.name}"? It is hidden from the list but nothing is deleted.`)) void act(() => api.archiveDocument(d.id, true)); }}>Archive</button>}
            </div>}
          </div>
        ))}
      </div>
    </>
  );
}

/** Loads every plan set's document JSON (takeoffs + markups live there). */
function useDocs(documents: DocumentSummary[], tick: number) {
  const [docs, setDocs] = useState<Array<{ meta: DocumentSummary; doc: CenterlineDocument }> | null>(null);
  const key = documents.map((d) => `${d.id}:${d.updatedAt}`).join(",");
  useEffect(() => {
    let off = false;
    Promise.all(documents.map((d) => api.document(d.id).then((r) => ({ meta: d, doc: r.document })))).then((x) => { if (!off) setDocs(x); }).catch(() => { if (!off) setDocs([]); });
    return () => { off = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, tick]);
  return docs;
}
const pageIdx = (doc: CenterlineDocument, pageId: string) => Math.max(0, doc.plan.pages.findIndex((p) => p.pageId === pageId));

/** Rename takeoffs from the list: one patchMeasurement op per item (same op the plan viewer uses, so it syncs live and shows in history). */
async function renameTakeoffs(items: Array<{ docId: string; m: Measurement }>, label: string): Promise<number> {
  const byDoc = new Map<string, Measurement[]>();
  for (const it of items) byDoc.set(it.docId, [...(byDoc.get(it.docId) ?? []), it.m]);
  let changed = 0;
  for (const [docId, ms] of byDoc) {
    const fresh = await api.document(docId);
    const ops = ms.flatMap((m) => {
      const cur = fresh.document.measurements.find((x) => x.id === m.id);
      const set = cur ? measurementChanges(cur, { label }) : {};
      return Object.keys(set).length ? [{ opId: newItemId().toLowerCase(), op: { type: "patchMeasurement" as const, id: m.id, set }, at: new Date().toISOString() }] : [];
    });
    if (!ops.length) continue;
    const body = await api.submitOps(docId, { baseSeq: (fresh.meta as { headSeq?: number }).headSeq ?? 0, clientId: clientId(), actor: actorName(), ops });
    const bad = body.results.find((x) => x.status === "rejected");
    if (bad) throw new Error(bad.message || "The server refused the rename.");
    changed += ops.length;
  }
  return changed;
}

function TakeoffsTab({ documents, tick }: { documents: DocumentSummary[]; tick: number }) {
  const [localTick, setLocalTick] = useState(0);
  const docs = useDocs(documents, tick + localTick);
  const [group, setGroup] = useState(true);
  const [modal, setModal] = useState<ModalSpec | null>(null);
  const [note, setNote] = useState<string>("");
  const rows = useMemo(() => (docs ?? []).flatMap(({ meta, doc }) => doc.measurements.filter((m) => m.kind !== "arrow").map((m) => {
    const v = realWorldValue(m, doc.pageScales[m.pageId]);
    return { m, meta, doc, i: pageIdx(doc, m.pageId), qty: v?.value ?? null, unit: m.payItem?.unit || v?.unit || "", key: takeoffGroupKey(m, v?.unit ?? "") };
  })), [docs]);
  const groups = useMemo(() => {
    const g = new Map<string, { key: string; label: string; pay?: string; labels: Set<string>; unit: string; qty: number; n: number; unscaled: number; items: Array<{ docId: string; m: Measurement }> }>();
    for (const r of rows) {
      const x = g.get(r.key) ?? { key: r.key, label: r.m.payItem?.name || r.m.label || r.m.kind, pay: r.m.payItem?.number, labels: new Set<string>(), unit: r.unit, qty: 0, n: 0, unscaled: 0, items: [] };
      x.n++; if (r.qty === null) x.unscaled++; else x.qty += r.qty;
      x.labels.add(r.m.label); x.items.push({ docId: r.meta.id, m: r.m });
      g.set(r.key, x);
    }
    return [...g.values()].sort((a, b) => (a.pay ?? "~").localeCompare(b.pay ?? "~", undefined, { numeric: true }) || a.label.localeCompare(b.label));
  }, [rows]);
  const rename = (items: Array<{ docId: string; m: Measurement }>, current: string) => setModal({
    title: items.length > 1 ? `Rename ${items.length} takeoffs` : "Rename takeoff",
    message: items.length > 1 ? "Every takeoff in this row gets the new label. Rows with the same label are totaled together." : "Takeoffs with the same label (and no pay item) are totaled together.",
    fields: [{ name: "label", label: "Label", value: current }],
    submit: "Rename",
    onSubmit: (v) => {
      const label = String(v.label ?? "").trim();
      if (!label) return "Type a label.";
      setNote("Saving…");
      renameTakeoffs(items, label).then((n) => { setNote(n ? `Renamed ${n} takeoff${n === 1 ? "" : "s"} to “${label}”.` : "Nothing changed."); setLocalTick((t) => t + 1); })
        .catch((e) => setNote((e as Error).message));
    },
  });
  if (!docs) return <div className="muted">Loading takeoffs…</div>;
  if (!rows.length) return <div className="empty">No takeoffs yet. Open a plan set and use Linear, Area or Count (works with finger, mouse or Apple Pencil).</div>;
  return (
    <>
      <div className="row"><label><input type="checkbox" checked={group} onChange={(e) => setGroup(e.target.checked)} /> Total alike items (by pay item, else label)</label></div>
      {note && <div className="muted small" role="status" data-testid="takeoff-note">{note}</div>}
      <div className="table-wrap card">
        {group ? <table key="totals" className="dr-table takeoff-totals">
          <thead><tr><th>Pay item</th><th>Description</th><th className="num">Takeoffs</th><th className="num">Total</th><th>Unit</th><th aria-label="Rename"></th></tr></thead>
          <tbody>{groups.map((g) => <tr key={g.key} data-key={g.key}><td>{g.pay ?? ""}</td><td>{g.label}{g.pay && [...g.labels].some((l) => l && l !== g.label) ? <span className="muted small"> · {[...g.labels].filter(Boolean).join(", ")}</span> : null}</td><td className="num">{g.n}{g.unscaled ? ` (${g.unscaled} unscaled)` : ""}</td><td className="num">{(Math.round(g.qty * 100) / 100).toLocaleString()}</td><td>{g.unit}</td>
            <td><button className="rename-row" aria-label={`Rename ${g.label}`} onClick={() => rename(g.items, g.items.length && g.labels.size === 1 ? [...g.labels][0]! : g.label)}>✎ Rename</button></td></tr>)}</tbody>
        </table> : <table key="list" className="dr-table">
          <thead><tr><th>Label</th><th>Pay item</th><th>Kind</th><th>Sheet</th><th className="num">Quantity</th><th>Unit</th><th>Layer</th><th aria-label="Rename"></th></tr></thead>
          <tbody>{rows.map((r) => <tr key={r.m.id} onClick={() => navigate({ name: "viewer", id: r.meta.id, page: r.i + 1, sel: r.m.id })}>
            <td>{r.m.label}</td><td>{r.m.payItem ? `${r.m.payItem.number} ${r.m.payItem.name}` : ""}</td><td>{r.m.kind}</td><td>{sheetNumberText(r.doc, r.i)} <span className="muted small">{r.meta.name}</span></td>
            <td className="num">{r.qty === null ? <span className="muted">unscaled</span> : (Math.round(r.qty * 100) / 100).toLocaleString()}</td><td>{r.unit}</td><td className="muted">{r.m.layer}</td>
            <td><button className="rename-row" aria-label={`Rename ${r.m.label}`} onClick={(e) => { e.stopPropagation(); rename([{ docId: r.meta.id, m: r.m }], r.m.label); }}>✎ Rename</button></td></tr>)}</tbody>
        </table>}
      </div>
      {modal && <Modal spec={modal} onClose={() => setModal(null)} />}
    </>
  );
}

function MarkupsTab({ documents, tick }: { documents: DocumentSummary[]; tick: number }) {
  const docs = useDocs(documents, tick);
  const [authors, setAuthors] = useState<Record<string, { by: string; at: string }>>({});
  const [layer, setLayer] = useState("");
  useEffect(() => { Promise.all(documents.map((d) => api.authors(d.id).then((r) => r.authors).catch(() => ({})))).then((all) => setAuthors(Object.assign({}, ...all))); }, [documents, tick]);
  if (!docs) return <div className="muted">Loading markups…</div>;
  const rows = docs.flatMap(({ meta, doc }) => doc.markups.map((m) => ({ m, meta, doc, i: pageIdx(doc, m.pageId) })));
  const layers = [...new Set(rows.map((r) => r.m.layer))].sort();
  const shown = rows.filter((r) => !layer || r.m.layer === layer);
  if (!rows.length) return <div className="empty">No markups yet. Open a plan set to add clouds, text, arrows, highlights and stamps.</div>;
  const label = (m: (typeof rows)[number]["m"]) => m.kind === "note" ? `Text: ${m.style.text.slice(0, 60)}` : m.kind === "approvalStamp" ? `Stamp: ${m.style.title}` : m.kind === "quickMeasure" ? "Quick measure" : m.kind === "highlight" ? "Highlight" : m.kind === "cloud" ? "Revision cloud" : m.kind;
  return (
    <>
      <div className="chips">
        <button className={!layer ? "chip on" : "chip"} onClick={() => setLayer("")}>All layers ({rows.length})</button>
        {layers.map((l) => <button key={l} className={layer === l ? "chip on" : "chip"} onClick={() => setLayer(l)}>{l} ({rows.filter((r) => r.m.layer === l).length})</button>)}
      </div>
      <div className="table-wrap card"><table className="dr-table">
        <thead><tr><th>Markup</th><th>Layer</th><th>Sheet</th><th>Added by</th><th>When</th></tr></thead>
        <tbody>{shown.map((r) => <tr key={r.m.id} onClick={() => navigate({ name: "viewer", id: r.meta.id, page: r.i + 1, sel: r.m.id })}>
          <td>{label(r.m)}</td><td>{r.m.layer}</td><td>{sheetNumberText(r.doc, r.i)} <span className="muted small">{r.meta.name}</span></td>
          <td>{authors[r.m.id]?.by ?? <span className="muted">imported</span>}</td><td className="muted small">{new Date(authors[r.m.id]?.at ?? r.m.createdAt).toLocaleString()}</td></tr>)}</tbody>
      </table></div>
    </>
  );
}

function PinsTab({ projectId, documents, tick }: { projectId: string; documents: DocumentSummary[]; tick: number }) {
  const [pins, setPins] = useState<Pin[] | null>(null);
  const [type, setType] = useState<PinType | "">("");
  const [status, setStatus] = useState("");
  const docs = useDocs(documents, 0);
  useEffect(() => { api.pins(projectId).then((r) => setPins(r.pins)).catch(() => setPins([])); }, [projectId, tick]);
  if (!pins) return <div className="muted">Loading pins…</div>;
  const shown = pins.filter((p) => (!type || p.type === type) && (!status || p.status === status));
  const where = (p: Pin) => {
    const d = docs?.find((x) => x.meta.id === p.documentId);
    if (!d) return { text: "", page: 1 };
    const i = pageIdx(d.doc, p.pageId);
    return { text: `${sheetNumberText(d.doc, i)} · ${d.meta.name}`, page: i + 1 };
  };
  return (
    <>
      <div className="chips">
        <button className={!type ? "chip on" : "chip"} onClick={() => setType("")}>All ({pins.length})</button>
        {PIN_TYPES.map((t) => <button key={t} className={type === t ? "chip on" : "chip"} onClick={() => setType(t)}><span className="pin-swatch" style={{ background: PIN_COLORS[t] }} />{t} ({pins.filter((p) => p.type === t).length})</button>)}
        <select value={status} onChange={(e) => setStatus(e.target.value)}><option value="">Any status</option><option value="open">Open</option><option value="in progress">In progress</option><option value="closed">Closed</option></select>
      </div>
      {!shown.length && <div className="empty">No pins{type ? ` of type ${type}` : ""}. Open a sheet, choose 📍 Pin, and tap the spot.</div>}
      <div className="pin-list">
        {shown.map((p) => { const w = where(p); return (
          <a key={p.id} className="card pin-row" href={href({ name: "viewer", id: p.documentId, page: w.page, pin: p.id })}>
            <span className="pin-swatch big" style={{ background: PIN_COLORS[p.type] }} />
            <div className="grow"><div className="title">{p.title}</div><div className="pin-meta"><span className="pin-type">{p.type}</span> · {w.text} · by {p.createdBy}{p.attachments.length ? ` · 📎 ${p.attachments.length}` : ""}</div></div>
            <span className={`status-chip ${p.status === "closed" ? "approved" : p.status === "in progress" ? "submitted" : ""}`}>{p.status}</span>
          </a>); })}
      </div>
    </>
  );
}
